import { v1 as uuid } from 'uuid';
import { isEmpty, forEach, find } from 'lodash';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import {
  QUESTION_CONVERSATIONAL,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_MATRIX_OPEN_ENDED,
} from 'data/nodeTypes';
import { ANSWER_DEFAULT } from 'containers/QuestionnairePage/constants';
import {
  isItemFromCurrentRevision,
  isItemInsideGroup,
} from 'containers/Permissions/helpers';
import {
  ANSWER_COLUMN_VALUE_TYPES,
  MATRIX_OPEN_ENDED_MIN_SCALEPOINTS,
  MATRIX_OPEN_ENDED_MIN_STATEMENTS,
  MATRIX_CONSTANT_SUM_MIN_SCALEPOINTS,
  MATRIX_CONSTANT_SUM_MIN_STATEMENTS,
  MATRIX_OPEN_ENDED_DEFAULT_COUNT,
  MATRIX_CONSTANT_SUM_DEFAULT_COUNT_STATEMENTS,
  MATRIX_CONSTANT_SUM_DEFAULT_COUNT_SCALEPOINTS,
  MATRIX_CONVERSATIONAL_DEFAULT_COUNT_STATEMENTS,
  MATRIX_CONVERSATIONAL_DEFAULT_COUNT_SCALEPOINTS,
  MATRIX_CONVERSATIONAL_CELL_TYPES,
  MATRIX_OPEN_ENDED_DEFAULT_COUNT_STATEMENTS,
  MATRIX_OPEN_ENDED_DEFAULT_COUNT_SCALEPOINTS,
} from './constants';

export const getEmptyAnswer = () => ({
  body: '',
  refId: uuid(),
  type: ANSWER_DEFAULT,
});

export const getEmptyMatrixOpenEndedAnswer = (answerRefId) => ({
  refId: uuid(),
  answerRefId,
  beforeLabel: '',
});

export const isMatrixAnswerEmpty = (matrixAnswer) => {
  const deserializedValue = getPromptFromHtml(matrixAnswer.beforeLabel);
  return isEmpty(deserializedValue);
};

export const getEmptyMatrixOpenEndedColumn = () => ({
  refId: uuid(),
  beforeLabel: '',
});

export const getAnswerValueColumn = ({
  answerRefId,
  columnRefId,
  type = ANSWER_COLUMN_VALUE_TYPES.NUMBER,
  cellType,
}) => {
  const answerValueColumn = {
    refId: uuid(),
    answerRefId,
    columnRefId,
    type,
    isMandatory: true,
  };

  if (cellType) {
    answerValueColumn.cellType = cellType;
  }

  return answerValueColumn;
};

export const getAnswerValueColumnTypesOpenEnded = ({
  answers,
  columns,
  type,
  answerColumnValueTypes: prevAnswerColumnValueTypes = [],
  questionType,
}) => {
  const answerColumnValueTypes = [];
  const isConversationalQuestion = questionType === QUESTION_CONVERSATIONAL;
  let addedGoalAnswer = false;

  forEach(answers, (answer) => {
    forEach(columns, (column) => {
      const existingAnswerValueColumnType = find(
        prevAnswerColumnValueTypes,
        (item) =>
          item.answerRefId === answer.refId &&
          item.columnRefId === column.refId,
      );

      if (existingAnswerValueColumnType) {
        answerColumnValueTypes.push(existingAnswerValueColumnType);
      } else {
        const cellType = addedGoalAnswer
          ? MATRIX_CONVERSATIONAL_CELL_TYPES.OBJECTIVE
          : MATRIX_CONVERSATIONAL_CELL_TYPES.GOAL;
        if (!addedGoalAnswer) {
          addedGoalAnswer = true;
        }

        answerColumnValueTypes.push(
          getAnswerValueColumn({
            answerRefId: answer.refId,
            columnRefId: column.refId,
            type,
            cellType: isConversationalQuestion ? cellType : undefined,
          }),
        );
      }
    });
  });

  return answerColumnValueTypes;
};

export const answersToBatchText = (
  rows = [],
  columns = [],
  shouldCleanHtml = false,
) => {
  const rowsText = rows.reduce((text, row) => {
    const body = (row.beforeLabel || '').trim();
    if (!body) {
      return text;
    }
    const cleanBody = shouldCleanHtml ? getPromptFromHtml(body) : body;
    return `${text}${cleanBody}\n`;
  }, '');

  const columnsText = columns.reduce((text, column) => {
    const body = (column.beforeLabel || '').trim();
    if (!body) {
      return text;
    }
    const cleanBody = shouldCleanHtml ? getPromptFromHtml(body) : body;
    return `${text}${cleanBody}\n`;
  }, '');

  return {
    rowsText: rowsText.trim(),
    columnsText: columnsText.trim(),
  };
};

export const isCarriedForward = (answer) => Boolean(answer.isCarriedForward);

export function getMinimumColumnsAndAnswers({
  matrixAnswers,
  columns,
  answers,
  questionRefId,
  questionType,
}) {
  const minimumStatementCount = getMinStatementsCount(questionType);
  const minimumScalepointCount = getMinScalepointsCount(questionType);

  return getNColumnsAndAnswers({
    matrixAnswers,
    columns,
    answers,
    questionRefId,
    questionType,
    answerCount: minimumStatementCount,
    columnCount: minimumScalepointCount,
  });
}
export function getDefaultColumnsAndAnswers({
  matrixAnswers,
  columns,
  answers,
  questionType,
}) {
  const defaultStatementCount = getDefaultStatementsCount(questionType);
  const defaultScalepointCount = getDefaultScalepointsCount(questionType);

  return getNColumnsAndAnswers({
    matrixAnswers,
    columns,
    answers,
    questionType,
    answerCount: defaultStatementCount,
    columnCount: defaultScalepointCount,
  });
}

export const getNColumnsAndAnswers = ({
  matrixAnswers,
  columns,
  answers,
  answerCount,
  columnCount,
}) => {
  let needsUpdating = false;
  const newAnswers = [...answers];
  const newMatrixAnswers = [...matrixAnswers];
  const newColumns = [...columns];
  const numberOfAnswersToAdd = answerCount - matrixAnswers.length;
  if (numberOfAnswersToAdd > 0) {
    needsUpdating = true;
    for (let i = 0; i < numberOfAnswersToAdd; i += 1) {
      const answer = getEmptyAnswer();
      newAnswers.push(answer);
      newMatrixAnswers.push(getEmptyMatrixOpenEndedAnswer(answer.refId));
    }
  }

  const numberOfColumnsToAdd = columnCount - columns.length;
  if (numberOfColumnsToAdd > 0) {
    needsUpdating = true;
    for (let i = 0; i < numberOfColumnsToAdd; i += 1) {
      newColumns.push(getEmptyMatrixOpenEndedColumn());
    }
  }

  return {
    needsUpdating,
    newAnswers,
    newMatrixAnswers,
    newColumns,
  };
};

export const getMinScalepointsCount = (type) => {
  switch (type) {
    case QUESTION_MATRIX_CONSTANT_SUM:
      return MATRIX_CONSTANT_SUM_MIN_SCALEPOINTS;
    default:
      return MATRIX_OPEN_ENDED_MIN_SCALEPOINTS;
  }
};

export const getMinStatementsCount = (type) => {
  switch (type) {
    case QUESTION_MATRIX_CONSTANT_SUM:
      return MATRIX_CONSTANT_SUM_MIN_STATEMENTS;
    default:
      return MATRIX_OPEN_ENDED_MIN_STATEMENTS;
  }
};

export const getDefaultScalepointsCount = (type) => {
  switch (type) {
    case QUESTION_MATRIX_OPEN_ENDED:
      return MATRIX_OPEN_ENDED_DEFAULT_COUNT_SCALEPOINTS;
    case QUESTION_MATRIX_CONSTANT_SUM:
      return MATRIX_CONSTANT_SUM_DEFAULT_COUNT_SCALEPOINTS;
    case QUESTION_CONVERSATIONAL:
      return MATRIX_CONVERSATIONAL_DEFAULT_COUNT_SCALEPOINTS;
    default:
      return MATRIX_OPEN_ENDED_DEFAULT_COUNT;
  }
};

export const getDefaultStatementsCount = (type) => {
  switch (type) {
    case QUESTION_MATRIX_OPEN_ENDED:
      return MATRIX_OPEN_ENDED_DEFAULT_COUNT_STATEMENTS;
    case QUESTION_MATRIX_CONSTANT_SUM:
      return MATRIX_CONSTANT_SUM_DEFAULT_COUNT_STATEMENTS;
    case QUESTION_CONVERSATIONAL:
      return MATRIX_CONVERSATIONAL_DEFAULT_COUNT_STATEMENTS;
    default:
      return MATRIX_OPEN_ENDED_DEFAULT_COUNT;
  }
};

export const canRemoveItem = ({ isUnderEdit, itemToCheck }) => {
  if (itemToCheck.isCarriedForward) {
    return false;
  }
  if (isUnderEdit && !isItemFromCurrentRevision({ itemToCheck })) {
    return false;
  }

  return true;
};

export const canAddStatementItem = ({ isUnderEdit, itemToCheck }) => {
  if (!isUnderEdit) {
    return true;
  }

  if (isItemFromCurrentRevision({ itemToCheck })) {
    return true;
  }

  if (!isItemInsideGroup({ itemToCheck })) {
    return true;
  }

  return false;
};

export const canAddScalepointItem = ({ isUnderEdit, itemToCheck }) => {
  if (!isUnderEdit) {
    return true;
  }

  if (isItemFromCurrentRevision({ itemToCheck })) {
    return true;
  }

  const allowScalepointByQuestionType = [QUESTION_MATRIX_OPEN_ENDED].includes(
    itemToCheck.type,
  );

  if (allowScalepointByQuestionType && !isItemInsideGroup({ itemToCheck })) {
    return true;
  }

  return false;
};
